import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

/*
 * List row: one object, its summary, its owner or audience, its state, and one
 * open affordance.
 *
 * Laid out as a grid rather than a flex row so that marks, chips, and arrows
 * line up down a list even when titles wrap. Every list in the portal is this
 * row with different slots filled, which is why Quick Wins, Projects,
 * requests, sources, learning, and activity need no list of their own.
 */

export interface RowMeta {
  label: string;
  value: React.ReactNode;
  foot?: React.ReactNode;
}

export interface ListRowProps {
  mark?: React.ReactNode;
  /** The metadata line above the title: type, time, scope. */
  kicker?: React.ReactNode;
  title: React.ReactNode;
  badge?: React.ReactNode;
  desc?: React.ReactNode;
  /** The owner or scope line under the title. */
  foot?: React.ReactNode;
  meta?: RowMeta[];
  /** Pixels per meta column, one for all or one each. Give every row of a list the same value so its columns align. */
  metaWidth?: number | number[];
  right?: React.ReactNode;
  /** Pixels reserved for `right` on wide screens, so rows with different action sets still line up. */
  rightWidth?: number;
  /** One line each for title and description, and columns that never shrink: for lists where every row must line up. */
  dense?: boolean;
  arrow?: boolean;
  to?: string;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}

// Wide enough for columns, they are columns. Narrower, the same facts wrap
// under the title as label/value pairs rather than disappearing: scope,
// audience and setup are the three things the row exists to answer, and a
// viewport is not a reason to stop answering them.
// A fixed width lines each column up down the list; left to size itself, a
// column starts wherever the longest value in that row pushed it. The width is
// a flex basis, not a hard size: when the row is too narrow for every column at
// full width, the columns give way together (still aligned, since every row in
// a list shares the same bases) and the title keeps a readable minimum instead
// of collapsing to a word per line with the badge bleeding under the columns.
const columnWidth = (width: number | number[] | undefined, i: number): number | undefined => {
  if (Array.isArray(width)) return width[i];
  return width;
};

const COLUMN_GAP = 20;

// The block's own basis is the sum of its columns. Left to `auto`, a flex
// container sizes from its text, not from its children's bases, so the block
// came out a different width on every row and nothing lined up.
const columnsBasis = (meta: RowMeta[], width: number | number[] | undefined, rigid?: boolean): React.CSSProperties | undefined => {
  if (!width) return undefined;
  const total = meta.reduce((sum, _m, i) => sum + (columnWidth(width, i) || 0), 0);
  let shrink = 1;
  if (rigid) shrink = 0;
  return { flex: `0 ${shrink} ${total + COLUMN_GAP * (meta.length - 1)}px` };
};

const MetaColumns: React.FC<{ meta: RowMeta[]; width?: number | number[]; rigid?: boolean }> = ({ meta, width, rigid }) => (
  <div className={`hidden min-w-0 items-start lg:flex ${width ? 'gap-5' : 'gap-7'}`} style={columnsBasis(meta, width, rigid)}>
    {meta.map((m, i) => (
      <div
        key={`${m.label}-${i}`}
        className={columnWidth(width, i) ? 'min-w-0 [overflow-wrap:anywhere]' : 'min-w-[88px]'}
        style={columnWidth(width, i) ? { flex: `0 ${rigid ? 0 : 1} ${columnWidth(width, i)}px` } : undefined}
      >
        <div className="text-[11px] font-semibold uppercase leading-[18.5px] tracking-[0.1em] text-ink-soft">{m.label}</div>
        <div className="mt-0.5 text-[13px] font-semibold leading-[21.5px] text-ink">{m.value}</div>
        {m.foot && <div className="text-[12px] leading-[20px] text-ink-dim">{m.foot}</div>}
      </div>
    ))}
  </div>
);

const MetaInline: React.FC<{ meta: RowMeta[] }> = ({ meta }) => (
  <dl className="m-0 mt-2 flex flex-wrap gap-x-4 gap-y-1 lg:hidden">
    {meta.filter((m) => m.label).map((m) => (
      <div key={m.label} className="flex items-baseline gap-1.5">
        <dt className="text-[11px] font-semibold uppercase leading-[18.5px] tracking-[0.1em] text-ink-soft">{m.label}</dt>
        <dd className="m-0 text-[12.5px] font-semibold leading-[20.75px] text-ink">{m.value}</dd>
      </div>
    ))}
  </dl>
);

export const ListRow: React.FC<ListRowProps> = ({
  mark, kicker, title, badge, desc, foot, meta, metaWidth, right, rightWidth, dense = false, arrow = false,
  to, onClick, disabled = false, className = '',
}) => {
  const body = (
    <>
      {mark}
      {/* The 220px basis lets the actions wrap under the text on a phone
          instead of squeezing it into a narrow column. */}
      <div className={`min-w-0 flex-1 basis-[220px] lg:min-w-[220px] ${dense ? 'md:basis-0' : 'md:basis-auto'}`}>
        {kicker && <div className="mb-0.5 text-[11px] leading-[18.5px] text-ink-soft">{kicker}</div>}
        <div className={`flex items-center gap-2 ${dense ? 'flex-nowrap' : 'flex-wrap'}`}>
          <span className={`text-[13px] font-semibold leading-[21.5px] text-ink ${dense ? 'truncate' : ''}`}>{title}</span>
          {badge}
        </div>
        {desc && <p className={`mt-1 text-[13px] leading-[21.5px] text-ink-dim ${dense ? 'line-clamp-1' : 'line-clamp-2'}`}>{desc}</p>}
        {foot && <div className="mt-0.5 text-[12px] leading-[20px] text-ink-dim">{foot}</div>}
        {meta && meta.length > 0 && <MetaInline meta={meta} />}
      </div>
      {meta && meta.length > 0 && <MetaColumns meta={meta} width={metaWidth} rigid={dense} />}
      {right && (
        <div
          className={`flex shrink-0 items-center justify-end ${rightWidth ? 'lg:min-w-[var(--row-right)]' : ''}`}
          style={rightWidth ? ({ '--row-right': `${rightWidth}px` } as React.CSSProperties) : undefined}
        >
          {right}
        </div>
      )}
      {arrow && <ArrowRight size={16} className="shrink-0 text-ink-soft" />}
    </>
  );

  // Flex rather than grid: a row has between three and six slots depending on
  // what it is showing, and a fixed template would push the extras onto a
  // second line. `min-w-0` on the body is what keeps long titles truncating
  // instead of shoving the chips off the end.
  const shell = `flex w-full flex-wrap items-start gap-x-3.5 gap-y-2 px-5 py-4 text-left transition-colors md:flex-nowrap md:items-center md:px-6 ${className}`;

  if (disabled) return <div className={`${shell} opacity-60`}>{body}</div>;
  if (to) return <Link to={to} className={`${shell} hover:bg-canvas`}>{body}</Link>;
  if (onClick) return <button type="button" onClick={onClick} className={`${shell} hover:bg-canvas`}>{body}</button>;
  return <div className={shell}>{body}</div>;
};
